import * as React from 'react'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import ListItemText from '@mui/material/ListItemText'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import classNames from 'classnames'
import iassign from 'immutable-assign'
import _ from 'lodash'
import validator from 'validator'

import { AccountTypes, PartnerAdmin, PattersonStaff, SimplifeyeStaff, SuperAdmin } from '../../models/enums'
import { CustomFieldType } from '../../models/enums'
import ConfirmModal from '../shared/confirm-modal/ConfirmModal'
import CustomField from '../shared/custom-fields/CustomField'
import CustomMultiselectField from '../shared/custom-fields/CustomMultiselectField'
import CustomTooltipV2 from '../shared/custom-tooltip-v2/CustomTooltipV2'

import AdminSuccessBox, { AdminSuccessBoxType } from './AdminSuccessBox'

import './ManageAdmin.sass'

export type ManageAdminProps = {
    admin?: Models.Account
    permissionList: Array<Models.Permission>
    submitButtonText: string
    onClose: () => void
    onSave: (state: Api.AccountUpdate) => void
    onSendPasswordReset?: () => Promise<void>
    onUnlockAccount?: () => Promise<void>
    showVyneAdmin?: boolean
    updateStatusOnUnlockAccount?: (adminId: string) => Promise<void>
}

export type ManageAdminDispatch = {}

const isEmail = (value: string) => {
    return validator.isEmail(value)
}

type AdminState = {
    name?: string
    username?: string
    permissions?: Array<Models.Permission>
    type?: string
}

export type ManageAdminState = {
    isDirty: boolean
    admin: AdminState
    showPasswordResetModal: boolean
    showPasswordUnlockModal: boolean
    successBox: AdminSuccessBoxType | ''
    allPermissionsSelected: boolean
    accountUnlocked: boolean
}

type Props = ManageAdminProps & ManageAdminDispatch

class ManageAdmin extends React.Component<Props, ManageAdminState> {
    constructor(props: Props) {
        super(props)
        this.state = {
            isDirty: false,
            admin: {},
            showPasswordResetModal: false,
            showPasswordUnlockModal: false,
            successBox: '',
            allPermissionsSelected: false,
            accountUnlocked: false,
        }
    }

    get isUsernameValid() {
        const username = !this.state.admin.username ? this.props.admin?.username : this.state.admin.username
        return Boolean(username && isEmail(username))
    }

    get canSave() {
        const { admin } = this.props
        const { name, username, permissions, type } = this.state.admin

        if (!admin) {
            return !_.isEmpty(name) && this.isUsernameValid && !_.isEmpty(type)
        }

        if (name === '' || username === '' || !this.isUsernameValid) {
            return false
        }

        return name !== admin.name || username !== admin.username || permissions != null
    }

    get isInvalidUsername() {
        if (this.state.admin && this.state.admin.username && this.state.admin.username !== '') {
            return !Boolean(isEmail(this.state.admin.username))
        }
        return false
    }

    get isSuperAdmin() {
        return this.props.admin?.type.id === AccountTypes.SuperAdmin
    }

    onSave = () => {
        if (this.isSuperAdmin) {
            return
        }

        if (this.canSave) {
            this.props.onSave({
                ...this.state.admin,
                permissions: this.state.admin.permissions && this.state.admin.permissions.map(p => p.type),
            })
            this.props.onClose()
        }
        return
    }

    confirmPasswordReset = () => {
        const { admin } = this.props
        if (!admin || !this.props.onSendPasswordReset) {
            return
        }

        this.props.onSendPasswordReset().then(() => {
            this.setState({ showPasswordResetModal: false })
            this.onShowSuccessBox('resetPassword')
        })
    }
    openPasswordResetModal = () => {
        this.setState({ showPasswordResetModal: true })
    }
    closePasswordResetModal = () => {
        this.setState({ showPasswordResetModal: false })
    }
    openPasswordUnlockModal = () => {
        this.setState({ showPasswordUnlockModal: true })
    }
    closePasswordUnlockModal = () => {
        this.setState({ showPasswordUnlockModal: false })
    }

    // onUnlockAccount = async () => {
    //     const { admin } = this.props

    //     if (!admin || !this.props.onUnlockAccount) {
    //         return
    //     }

    //     const accountName = admin.name.trim() ? `${admin.name}'s` : 'this'

    //     if (window.confirm(`Do you want to unlock ${accountName} account?`)) {
    //         await this.props.onUnlockAccount()
    //         window.alert(`Account unlocked.`)
    //     }
    // }

    onCloseSuccessBox = () => {
        this.setState({ successBox: '' })
    }

    onShowSuccessBox = (type: AdminSuccessBoxType) => {
        this.setState({ successBox: type })
    }

    confirmPasswordUnlock = () => {
        const { admin } = this.props
        if (!admin || !this.props.onUnlockAccount) {
            return
        }

        this.props.onUnlockAccount().then(() => {
            this.setState({ showPasswordUnlockModal: false, accountUnlocked: true })
            this.onShowSuccessBox('unlockPassword')
            if (this.props.updateStatusOnUnlockAccount) {
                this.props.updateStatusOnUnlockAccount(admin.id)
            }
        })
    }

    onChangeAdmin(modifier: (a: AdminState) => void) {
        if (this.isSuperAdmin) {
            return
        }

        this.setState(
            iassign(
                this.state,
                next => next.admin,
                next => {
                    modifier(next)
                    return next
                },
            ),
            () => this.setState({ isDirty: true }),
        )
    }

    toggleSelectAllPermissions = (checked: boolean) => {
        if (this.isSuperAdmin) {
            return
        }

        this.setState(
            iassign(
                this.state,
                next => next.admin,
                next => {
                    next.permissions = checked ? _.clone(this.props.permissionList) : []
                    return next
                },
            ),
            () => this.setState({ isDirty: true }),
        )
    }

    onSetPermission(permission: Models.Permission) {
        if (this.isSuperAdmin) {
            return
        }

        this.onChangeAdmin(a => {
            if (this.props.admin && a.permissions == null) {
                a.permissions = _.clone(this.props.admin.permissions)
            }

            if (a.permissions == null) {
                a.permissions = [permission]
                return
            }

            const i = a.permissions.findIndex(p => p.type === permission.type)
            if (i < 0) {
                a.permissions.push(permission)
            } else {
                a.permissions.splice(i, 1)
            }
        })
    }

    render() {
        const { permissionList, submitButtonText, admin: propsAdmin } = this.props

        const sortedPermissionList = permissionList.sort((a, b) => {
            return a.displayName.localeCompare(b.displayName)
        })

        const isInvited = propsAdmin?.accountStatus.value === 'invited'
        const isSuperAdmin = propsAdmin?.type.id === AccountTypes.SuperAdmin

        const admin =
            propsAdmin == null
                ? this.state.admin
                : {
                      name: this.state.admin.name ?? propsAdmin.name,
                      username: this.state.admin.username ?? propsAdmin.username,
                      permissions: this.state.admin.permissions || [...propsAdmin.permissions],
                      type: this.state.admin.type || propsAdmin.type.value,
                  }

        const adminTypes = [SimplifeyeStaff, PartnerAdmin, PattersonStaff]
        if (this.isSuperAdmin) {
            adminTypes.push(SuperAdmin)
        }

        return (
            <div className={classNames('manage-admin')}>
                <div className="content">
                    <div className="name-title" title={propsAdmin && propsAdmin.id}>
                        {propsAdmin ? propsAdmin.name : 'New Admin'}
                    </div>
                    <div className="fields">
                        <div className="name">
                            <TextField
                                fullWidth={true}
                                value={admin.name || ''}
                                onChange={e => this.onChangeAdmin(a => (a.name = e.target.value))}
                                label="First Name & Last Name"
                                variant="standard"
                                disabled={this.isSuperAdmin}
                            />
                        </div>
                        <div className="email">
                            <TextField
                                fullWidth={true}
                                type="email"
                                value={admin.username || ''}
                                onChange={e => this.onChangeAdmin(a => (a.username = e.target.value))}
                                label="Email"
                                variant="standard"
                                disabled={this.isSuperAdmin}
                            />
                            {this.isInvalidUsername && <span className="email-error">Email address is not valid</span>}
                        </div>
                    </div>
                    {propsAdmin && (
                        <>
                            <div className="password-reset">
                                <Button className="send-reset" onClick={this.openPasswordResetModal}>
                                    {isInvited ? 'Resend Invite' : 'Password Reset'}
                                </Button>
                            </div>

                            <ConfirmModal
                                title={
                                    isInvited
                                        ? `Resend invite to ${admin.name} at ${admin.username}?`
                                        : `Send password reset to ${admin.name} at ${admin.username}?`
                                }
                                subtitle=""
                                discardText="Cancel"
                                confirmText="Confirm"
                                open={this.state.showPasswordResetModal}
                                onClose={this.closePasswordResetModal}
                                onConfirm={this.confirmPasswordReset}
                                onDiscard={this.closePasswordResetModal}
                            />
                        </>
                    )}
                    {propsAdmin?.locked && !this.state.accountUnlocked && (
                        <>
                            <div className="password-reset">
                                <Button className="send-reset" onClick={this.openPasswordUnlockModal}>
                                    Unlock Account
                                </Button>
                            </div>
                            <ConfirmModal
                                title={`Do you want to unlock ${admin.name}'s account?`}
                                subtitle=""
                                discardText="Cancel"
                                confirmText="Confirm"
                                open={this.state.showPasswordUnlockModal}
                                onClose={this.closePasswordUnlockModal}
                                onConfirm={this.confirmPasswordUnlock}
                                onDiscard={this.closePasswordUnlockModal}
                            />
                        </>
                    )}
                    <div className={`account-types ${propsAdmin ? ' with-reset-password' : ''}`}>
                        <div className="types">
                            <CustomMultiselectField
                                items={adminTypes}
                                maxSelected={1}
                                selectedItems={[admin.type || '']}
                                keyProperty="value"
                                displayProperty="displayName"
                                placeholder="Select Account Type"
                                label="Account Type"
                                disabled={propsAdmin != null || this.isSuperAdmin}
                                onSelectElement={(values: string[]) => this.onChangeAdmin(a => (a.type = values[0]))}
                            />
                        </div>
                        <div className="tooltip-simplifeye-staff">
                            <CustomTooltipV2
                                tooltipText={
                                    <div>
                                        A ‘Vyne Staff’ member is a person who is a direct employee of Vyne.
                                        <br />
                                        They have an elevated set of permissions and actions compared to a ‘Partner
                                        Admin’ by default.
                                        <br />A ‘Patterson Staff’ user could see only Patterson-supported customers.
                                    </div>
                                }
                                type="click"
                            />
                        </div>
                    </div>
                    <div className="permission-types">
                        <div className="label-wrapper">
                            <div className="label">Permission Types</div>
                            <div className="select-all">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={(_1, checked) => this.toggleSelectAllPermissions(checked)}
                                            checked={
                                                admin.permissions?.filter(p => p.id !== 1)?.length ===
                                                permissionList.length
                                            }
                                            color="primary"
                                            disabled={this.isSuperAdmin}
                                        />
                                    }
                                    label="Select All"
                                />
                            </div>
                        </div>
                        <div className="permissions">
                            {sortedPermissionList.map(permission => (
                                <div key={permission.id} className="permission">
                                    <Checkbox
                                        color="primary"
                                        checked={
                                            (admin.permissions &&
                                                admin.permissions.find(p => p.type === permission.type) != null) ||
                                            false
                                        }
                                        onClick={() => this.onSetPermission(permission)}
                                        disabled={this.isSuperAdmin}
                                    />
                                    <ListItemText
                                        className="manage-admin__list-item-text"
                                        primary={permission.displayName}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {this.state.successBox && (
                    <Modal
                        open={true}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                this.onCloseSuccessBox()
                            }
                        }}
                        disableEscapeKeyDown={true}
                    >
                        <div className="staff-member-confirmation-modal">
                            <div className="close-modal-button" onClick={this.onCloseSuccessBox}>
                                <i className="material-icons">close</i>
                            </div>
                            <AdminSuccessBox
                                username={admin?.username}
                                name={admin?.name}
                                closeFn={this.onCloseSuccessBox}
                                type={this.state.successBox}
                                title={
                                    isInvited
                                        ? `An invitation has been re-sent to ”${admin.username}”`
                                        : `A password reset link has been sent to ”${admin.username}”`
                                }
                            />
                        </div>
                    </Modal>
                )}
                <button
                    className={classNames('bottom-save-button', { 'can-save': this.canSave && this.state.isDirty })}
                    disabled={!this.canSave || !this.state.isDirty || this.isSuperAdmin}
                    onClick={this.onSave}
                >
                    {submitButtonText}
                </button>
            </div>
        )
    }
}

export default ManageAdmin
